/**
 * MatchAuthority — the single place that decides who hurt whom, for how much, and
 * who is dead.
 *
 * ## Why this exists
 *
 * Until now every damageable thing judged its own case. `Ballistics` found an actor,
 * handed it a number, and the actor subtracted that from its own health and declared
 * its own death. That works exactly as long as there is one computer running the
 * game, and stops working the moment there are two: both machines would run the same
 * shot against slightly different positions and disagree about whether it killed
 * anyone. Disagreements about death are the one kind a shooter cannot paper over.
 *
 * It also could not answer two questions that team modes are entirely made of:
 *
 *   - **Who gets the kill?** No damage call site recorded an attacker. Only one thing
 *     in the game could kill (the player), so the answer was assumed rather than
 *     known. In Team Deathmatch a bot killing a bot must score for the bot's team,
 *     and must not light up your crosshair.
 *   - **Is this person on my side?** There were no sides. The only friendly-fire check
 *     in the codebase was the literal `hit.actor !== ctx.player`.
 *
 * So this module owns the registry of everyone who can be hurt, the team they are on,
 * and the arithmetic. Damage sources stop *applying* damage and start *requesting* it.
 *
 * ## The shape that makes networking possible later
 *
 * A `DamageRequest` is deliberately close to plain data: ids rather than object
 * references for the two actors, a hitbox *tag* rather than the hitbox object, a
 * `means` string. That is not tidiness for its own sake — it is the line along which
 * this splits when a second player arrives. A client sends requests; the host runs
 * `requestDamage` and replicates the outcome. Nothing above this file has to change,
 * because nothing above this file is allowed to decide the outcome any more.
 *
 * The one concession is `hitbox`, which is passed through as a live object. The
 * authority reads only `tag` and `damageScale` from it; everything else in there is a
 * bone reference the *target* needs for its flinch animation. A remote request will
 * carry the tag alone and the target will look its own hitbox up.
 *
 * ## What it does not own
 *
 * How much a bullet is worth at 40 metres, how an explosion falls off with distance,
 * whether a shotgun pellet even connects. Those stay with the weapon: they are the
 * weapon's design, and they are the same answer on every machine given the same
 * inputs. The authority arbitrates; it does not do ballistics.
 *
 * ## Rules
 *
 * Scoring, spawning, friendly fire and victory are not written here either. They are
 * a `rules` object (see `rules/`) that this module calls at four points. Swapping it
 * swaps the game mode without touching a single damage call site — which is the whole
 * reason to do this work before writing Team Deathmatch rather than after.
 */

export const TEAM = {
  /** Neutral: hurt by everyone, scores for no one. Barrels, and any future props. */
  NONE: -1,
  ALPHA: 0,
  BRAVO: 1,
};

/** How a wound was caused. Used for the killfeed and for rules that care. */
export const MEANS = {
  BULLET: 'bullet',
  MELEE: 'melee',
  EXPLOSIVE: 'explosive',
  BURN: 'burn',
  SHOCK: 'shock',
  /** The world itself: the kill plane, falling out of the map. */
  WORLD: 'world',
};

export class MatchAuthority {
  constructor(ctx, rules = null) {
    this.ctx = ctx;
    this.events = ctx.events;

    /** id -> registration. Keyed by id, not by object, so a remote id resolves too. */
    this.actors = new Map();

    /**
     * Deliberately not `physics.actors`.
     *
     * An enemy pulls itself out of the physics world the instant it dies, so its
     * corpse can be walked through. If the registry were the same map, the authority
     * would forget who it was one line before the killfeed asks for the name.
     */

    this.rules = rules ?? new Skirmish();
    this.rules.attach?.(this);
  }

  /**
   * Swap the game mode. Everything that deals damage keeps working across this,
   * because none of it knows what the rules are — which was the point of the whole
   * extraction.
   */
  setRules(rules) {
    this.rules?.dispose?.();
    this.rules = rules;
    this.rules.attach?.(this);
    this.events.emit('match:rules', { id: rules.id });
    return rules;
  }

  /* ------------------------------- registry ------------------------------- */

  /**
   * Enrol something that can be hurt.
   *
   * `actor` must have `id` and `health`, and should implement `applyDamage` and
   * `applyDeath` — see the contract at the bottom of this file.
   */
  register(actor, { team = TEAM.NONE, kind = 'actor', name = null, bot = false, owner = null } = {}) {
    const reg = {
      id: actor.id,
      actor,
      team,
      kind,
      bot,
      /**
       * Who this thing kills *for*. Null for anyone who fights on their own
       * account; a sentry gun's owner is the player who dropped it.
       *
       * Only exists because "on my team" and "mine" stopped being the same
       * sentence the moment there were teammates. See `creditedToLocal`.
       */
      owner,
      name: name ?? actor.id,
      /** The authority's own copy of "is this one out of the fight". `actor.dead` is
       * the actor's presentation state (ragdolling, corpse timer); this is the fact
       * that decides whether a second bullet can score a second kill. */
      dead: false,
    };
    this.actors.set(actor.id, reg);
    return reg;
  }

  unregister(id) {
    this.actors.delete(typeof id === 'string' ? id : id?.id);
  }

  /** Registration for an id, an actor object, or null. Never throws. */
  get(ref) {
    if (!ref) return null;
    if (typeof ref === 'string') return this.actors.get(ref) ?? null;
    return this.actors.get(ref.id) ?? null;
  }

  teamOf(ref) {
    return this.get(ref)?.team ?? TEAM.NONE;
  }

  /** Everyone alive on a team. Allocates; call it from setup, not from a hot loop. */
  living(team = null) {
    const out = [];
    for (const reg of this.actors.values()) {
      if (reg.dead) continue;
      if (team !== null && reg.team !== team) continue;
      out.push(reg);
    }
    return out;
  }

  /**
   * May `source` hurt `target`?
   *
   * Neutrals and unknown attackers can hurt anyone: an explosion with no owner still
   * has to be able to kill you, or a grenade rolled back at its thrower would be
   * harmless. Everything else is the rules' call.
   */
  hostile(source, target, splash = false) {
    const dst = this.get(target);
    if (!dst) return false;
    const src = this.get(source);
    if (!src) return true;
    if (src === dst) return this.rules.selfDamage !== false;
    /**
     * Splash is judged separately from aimed fire, because every shooter that has
     * ever turned friendly fire off has still let your own grenade kill you. Lumping
     * the two together would make a live frag a safe object to stand next to, which
     * removes the only interesting decision the weapon has.
     */
    if (splash) return this.rules.canSplash?.(src, dst) ?? true;
    return this.rules.canDamage(src, dst);
  }

  /* -------------------------------- damage -------------------------------- */

  /**
   * The one way anything in the game takes damage.
   *
   * Returns a resolution record (`{ blocked }`, or the amount dealt and whether it
   * killed) so a caller that needs to react — a hitmarker, a burn tick that should
   * stop once the target is down — can, without inspecting the target itself.
   *
   * @param {object} req
   * @param {object|string}      req.target  who is hit
   * @param {object|string|null} req.source  who did it; null for the world
   * @param {number}             req.amount  base damage, before the hitbox multiplier
   * @param {object|null}        req.hitbox  hitbox struck, if any
   * @param {string}             req.means   one of MEANS
   */
  requestDamage(req) {
    /**
     * In a networked match there is exactly one authority and it is the host.
     *
     * This is the seam the whole extraction existed for: a client does not decide,
     * it *asks*. The request is already close to plain data — ids, a hitbox tag, a
     * `means` — precisely so it could be put on a wire without being rebuilt, and
     * this is the line where that pays off. Nothing above here changes; a shot
     * fired on a client runs the identical code path it runs in single player.
     *
     * The caller still gets a truthful-looking result so the hitmarker appears
     * immediately, flagged `pending` because the damage has not been applied by
     * anybody yet. Waiting for the host's reply before drawing the marker would
     * put a round trip between the trigger and the feedback, which is the one
     * thing a shooter cannot afford to be honest about.
     */
    const net = this.ctx.net;
    if (net?.active && !net.isHost) {
      const forwarded = net.forwardDamage(req);
      if (forwarded) return forwarded;
    }

    const dst = this.get(req.target);
    if (!dst) {
      // Not an arbitration failure: streak weapons and explosions sweep lists that
      // can contain something that was never registered. Silently ignoring it would
      // hide a real wiring mistake, though, so say so once per unknown id.
      this._warnUnknown(req.target);
      return { blocked: true, reason: 'unknown' };
    }
    if (dst.dead) return { blocked: true, reason: 'dead' };

    const src = this.get(req.source);
    if (!this.hostile(src, dst, req.splash === true)) {
      return { blocked: true, reason: 'friendly' };
    }

    const scale = req.hitbox?.damageScale ?? 1;
    const amount = Math.max(0, (req.amount ?? 0) * scale);
    if (amount <= 0) return { blocked: true, reason: 'zero' };

    /**
     * No health pool means not a target: streaks register so they can *own* damage
     * and be credited for it, not so they can take it. Without this guard their
     * `undefined` health reads as zero and the first stray pellet declares a sentry
     * gun dead — with a killfeed line and a point for whoever fired it.
     */
    const before = dst.actor.health;
    if (typeof before !== 'number') return { blocked: true, reason: 'invulnerable' };

    const after = Math.max(0, before - amount);
    const fatal = after <= 0;

    const resolved = {
      target: dst,
      source: src,
      amount,
      before,
      after,
      fatal,
      headshot: req.hitbox?.tag === 'head',
      hitbox: req.hitbox ?? null,
      dir: req.dir ?? null,
      point: req.point ?? null,
      means: req.means ?? MEANS.BULLET,
      weapon: req.weapon ?? null,
    };

    // The actor writes the number and plays its reaction; it no longer judges
    // whether that number was lethal. Wrapped because a flinch animation throwing
    // must not swallow the death that came with it.
    try {
      dst.actor.applyDamage?.(resolved);
    } catch (err) {
      console.error('[match] applyDamage threw', err);
    }

    if (fatal) this._declareDeath(resolved);
    return resolved;
  }

  /**
   * Kill outright, bypassing health. The kill plane and any future execution use
   * this rather than requesting 999 damage, so a rule that caps damage or grants
   * armour cannot accidentally make falling out of the map survivable.
   */
  requestKill(target, { source = null, means = MEANS.WORLD } = {}) {
    const dst = this.get(target);
    if (!dst || dst.dead) return { blocked: true };
    const before = dst.actor.health ?? 0;
    const resolved = {
      target: dst, source: this.get(source),
      amount: before, before, after: 0, fatal: true,
      headshot: false, hitbox: null, dir: null, point: null,
      means, weapon: null,
    };
    try {
      dst.actor.applyDamage?.(resolved);
    } catch (err) {
      console.error('[match] applyDamage threw', err);
    }
    this._declareDeath(resolved);
    return resolved;
  }

  _declareDeath(resolved) {
    const dst = resolved.target;
    if (dst.dead) return;
    dst.dead = true;

    try {
      dst.actor.applyDeath?.(resolved);
    } catch (err) {
      console.error('[match] applyDeath threw', err);
    }

    // Rules score it before the event goes out, so a HUD listening for `actor:death`
    // paints a scoreboard that already includes this kill rather than the one before.
    try {
      this.rules.onKill?.(resolved, this);
    } catch (err) {
      console.error('[match] rules.onKill threw', err);
    }

    /**
     * `actor` stays an object reference, because every existing listener uses it for
     * a position, a name or an identity comparison, and changing that is a separate
     * job from moving the decision. `source` is the new part: before this, a listener
     * that wanted to know "did *I* get this kill" had to assume the answer.
     */
    this.events.emit('actor:death', {
      actor: dst.actor,
      source: resolved.source?.actor ?? null,
      sourceTeam: resolved.source?.team ?? TEAM.NONE,
      team: dst.team,
      headshot: resolved.headshot,
      means: resolved.means,
      weapon: resolved.weapon,
      mine: this.creditedToLocal(resolved),
    });

    // Only the host reaches this line in a networked match, so this is the one
    // place a death is announced and every client is told the same thing.
    this.ctx.net?.announceDeath?.(resolved);
  }

  /**
   * Did the player at this keyboard earn this kill?
   *
   * Eight listeners react to `actor:death`, and until now every one of them assumed
   * the answer was yes — reasonably, since the only thing that could kill was the
   * player and the player's own death went out under a different event name. Both
   * halves of that assumption stopped being true, so the question needs a real
   * answer in one place rather than eight guesses.
   *
   * The first version of this asked "is the killer on my team", which was right up
   * to the exact moment teams contained anybody else. Measured in a live Team
   * Deathmatch: two squadmates shot two hostiles across the map and the HUD gave
   * *me* two kills and 200 XP for standing still. Being on the same side is not
   * the same claim as having done it.
   *
   * A kill by your own sentry gun or gunship still counts — that is how it has
   * always scored and how the genre expects it to — which is what `owner` is for.
   * Killing your own side never counts.
   */
  creditedToLocal(resolved) {
    const localActor = this.ctx.player;
    const local = this.get(localActor);
    if (!local || !localActor) return false;
    const src = resolved.source;
    if (!src) return false;
    const byMe = src.actor === localActor || src.owner === localActor;
    if (!byMe) return false;
    return resolved.target.team !== local.team;
  }

  /** Put a registration back in the fight. The actor's own respawn does the rest. */
  revive(ref) {
    const reg = this.get(ref);
    if (reg) reg.dead = false;
    return reg;
  }

  _warnUnknown(ref) {
    const id = typeof ref === 'string' ? ref : ref?.id ?? '(no id)';
    this._warned ??= new Set();
    if (this._warned.has(id)) return;
    this._warned.add(id);
    console.warn(`[match] damage requested against unregistered actor "${id}"`);
  }

  dispose() {
    this.rules.dispose?.();
    this.actors.clear();
  }
}

/**
 * ## The rules contract
 *
 * A mode is an object with these five members. Every one has a working default here,
 * so a mode only writes the parts it actually changes — Domination is a `onKill` and
 * a scoring tick, not a rewrite of friendly fire.
 *
 * ```js
 * rules.friendlyFire      // boolean
 * rules.canDamage(src,dst)   // aimed fire. Default: different teams only.
 * rules.canSplash(src,dst)   // blast. Default: yes, always — your own frag counts.
 * rules.onKill(resolved, match)  // score it. Called before `actor:death` goes out.
 * rules.pickSpawn(reg, ctx)      // where this actor comes back. null = caller decides.
 * rules.checkWin(match)          // -> null | { team, reason }
 * ```
 *
 * `Skirmish` is what the game did before modes existed: one team of bots against
 * you, no team scoring here because `GameDirector` still counts to thirty. Keeping
 * it as the default is what makes extracting the authority a change with no visible
 * effect, which is the only way to know the extraction itself was correct.
 */
export class Skirmish {
  constructor() {
    this.id = 'skirmish';
    /** False: `GameDirector`'s thirty-kill target still ends this one. */
    this.ownsVictory = false;
    /** Splash from your own grenade hurts you — it always has. */
    this.selfDamage = true;
    this.friendlyFire = false;
  }

  canDamage(src, dst) {
    if (this.friendlyFire) return true;
    if (src.team === TEAM.NONE || dst.team === TEAM.NONE) return true;
    return src.team !== dst.team;
  }

  /** Blast ignores the friendly-fire switch. See `MatchAuthority.hostile`. */
  canSplash() {
    return true;
  }

  onKill() {}

  /**
   * Null means "you choose" — the director's existing furthest-from-danger search
   * stays in charge while there is only one spawn rule in the game. A mode with
   * team spawns or a bomb site answers this properly instead.
   */
  pickSpawn() {
    return null;
  }

  /** Null means the match is not over. Skirmish's win is still the kill target. */
  checkWin() {
    return null;
  }
}

/**
 * ## Actor contract
 *
 * ```js
 * actor.id                 // string, unique
 * actor.health             // number, the actor still stores it
 * actor.applyDamage(res)   // set health = res.after, react (flinch, audio, aggro).
 *                          // MUST NOT test for death — res.fatal is already decided.
 * actor.applyDeath(res)    // ragdoll, sounds, leave the physics world.
 *                          // MUST NOT emit `actor:death`; the authority does.
 * ```
 */
